import * as api from '../api';
import { TOKEN_HEADER } from '../constants';
import { getJwtToken } from '../storage';

// Multipart uploads bypass the saga wrapper that normally stamps the JWT, so
// it is attached here to avoid 401s from the gateway's auth handler.
const withAuthOptions = (callerOptions) => {
  const jwt = getJwtToken();
  return {
    ...(callerOptions || {}),
    headers: {
      ...((callerOptions && callerOptions.headers) || {}),
      [TOKEN_HEADER]: jwt,
    },
  };
};

// Templates are global, but the tasks and stock on them belong to sites: the
// backend returns only the named site's, and none when no site is named.
const withSiteQuery = (args = {}) => {
  const siteId = args.options && args.options.siteId;
  if (!siteId) return args;
  const data = args.data || {};
  return { ...args, data: { ...data, query: { ...(data.query || {}), siteId } } };
};

// The master template list. Templates are global blueprints; what a site
// actually runs lives in the occurrence layer, not here.
export const getAssetTemplates = (args) => {
  return api.callGet('/manager/asset-templates', withSiteQuery(args));
};

// What this site actually runs: the occurrence layer, not the blueprints.
export const getAssetTemplateOccurrences = (args) => {
  const {
    options: { siteId },
  } = args;
  return api.callGet(`/manager/sites/${siteId}/asset-template-occurrences`, args);
};

// Deploying a blueprint to a site. Idempotent on (siteId, assetTemplateId).
export const postAssetTemplateOccurrence = (args) => {
  const {
    options: { siteId },
  } = args;
  return api.callPost(`/manager/sites/${siteId}/asset-template-occurrences`, args);
};

export const getAssetTemplateOccurrence = (args) => {
  const {
    options: { siteId },
    data: { id },
  } = args;
  return api.callGet(`/manager/sites/${siteId}/asset-template-occurrences/${id}`, args);
};

// Per-site edits to a deployment: name, notes, status, washdown flag.
export const patchAssetTemplateOccurrence = (args) => {
  const {
    options: { siteId },
    data: { id },
  } = args;
  return api.callPatch(`/manager/sites/${siteId}/asset-template-occurrences/${id}`, args);
};

// Taking a template back off a site. Archives the occurrence subtree rather
// than deleting it, so completed task history survives.
export const archiveAssetTemplateOccurrence = (args) => {
  const {
    options: { siteId },
    data: { id },
  } = args;
  return api.callPost(`/manager/sites/${siteId}/asset-template-occurrences/${id}/archive`, {
    options: args.options,
  });
};

// Image upload is multipart, so it goes through the FormData helper rather
// than the JSON post used everywhere else in this file.
export const postAssetTemplateImage = (args) => {
  const {
    data: { id, file },
  } = args;
  const body = file instanceof FormData ? file : new FormData();
  if (!(file instanceof FormData)) body.append('file', file, file.name);
  return api.callPostWithFormData(`/manager/asset-templates/${id}/image`, {
    data: body,
    options: withAuthOptions(args.options),
  });
};

export const deleteAssetTemplateImage = (args) => {
  const {
    data: { id },
  } = args;
  return api.callDelete(`/manager/asset-templates/${id}/image`, { options: args.options });
};

export const getAssetTemplate = (args) => {
  const {
    data: { id },
  } = args;
  return api.callGet(`/manager/asset-templates/${id}`, withSiteQuery(args));
};

export const postAssetTemplate = (args) => {
  const {
    options: { siteId },
  } = args;
  return api.callPost(`/manager/sites/${siteId}/asset-templates`, args);
};

export const patchAssetTemplate = (args) => {
  const {
    data: { id },
  } = args;
  return api.callPatch(`/manager/asset-templates/${id}`, args);
};

export const archiveAssetTemplate = (args) => {
  const {
    data: { id },
  } = args;
  return api.callPost(`/manager/asset-templates/${id}/archive`, { options: args.options });
};

export const unarchiveAssetTemplate = (args) => {
  const {
    data: { id },
  } = args;
  return api.callPost(`/manager/asset-templates/${id}/unarchive`, { options: args.options });
};

export const linkTemplateGroup = (args) => {
  const {
    data: { id },
  } = args;
  return api.callPost(`/manager/asset-templates/${id}/groups`, args);
};

export const unlinkTemplateGroup = (args) => {
  const {
    data: { id, assetGroupId },
  } = args;
  return api.callDelete(`/manager/asset-templates/${id}/groups/${assetGroupId}`, {
    options: args.options,
  });
};

export const linkTemplateItem = (args) => {
  const {
    data: { id },
  } = args;
  return api.callPost(`/manager/asset-templates/${id}/items`, args);
};

export const unlinkTemplateItem = (args) => {
  const {
    data: { id, assetItemId },
  } = args;
  return api.callDelete(`/manager/asset-templates/${id}/items/${assetItemId}`, {
    options: args.options,
  });
};

export const getTaskTemplates = (args) => {
  const {
    data: { assetTemplateId },
  } = args;
  return api.callGet(`/manager/asset-templates/${assetTemplateId}/task-templates`, args);
};

export const getTaskTemplate = (args) => {
  const {
    data: { assetTemplateId, id },
  } = args;
  return api.callGet(`/manager/asset-templates/${assetTemplateId}/task-templates/${id}`, args);
};

export const postTaskTemplate = (args) => {
  const {
    data: { assetTemplateId },
  } = args;
  return api.callPost(`/manager/asset-templates/${assetTemplateId}/task-templates`, args);
};

export const patchTaskTemplate = (args) => {
  const {
    data: { assetTemplateId, id },
  } = args;
  return api.callPatch(`/manager/asset-templates/${assetTemplateId}/task-templates/${id}`, args);
};

export const archiveTaskTemplate = (args) => {
  const {
    data: { assetTemplateId, id },
  } = args;
  return api.callPost(`/manager/asset-templates/${assetTemplateId}/task-templates/${id}/archive`, {
    options: args.options,
  });
};

export const unarchiveTaskTemplate = (args) => {
  const {
    data: { assetTemplateId, id },
  } = args;
  return api.callPost(
    `/manager/asset-templates/${assetTemplateId}/task-templates/${id}/unarchive`,
    { options: args.options }
  );
};

export const getTemplateInventories = (args) => {
  const {
    data: { assetTemplateId },
  } = args;
  return api.callGet(
    `/manager/asset-templates/${assetTemplateId}/inventories`,
    withSiteQuery(args)
  );
};

export const linkTemplateInventory = (args) => {
  const {
    data: { assetTemplateId },
  } = args;
  return api.callPost(`/manager/asset-templates/${assetTemplateId}/inventories`, args);
};

export const unlinkTemplateInventory = (args) => {
  const {
    data: { assetTemplateId, inventoryId },
  } = args;
  return api.callDelete(`/manager/asset-templates/${assetTemplateId}/inventories/${inventoryId}`, {
    options: args.options,
  });
};

export const getAssetGroups = (args) => {
  const {
    options: { siteId },
  } = args;
  return api.callGet(`/manager/sites/${siteId}/asset-groups`, args);
};

export const getAssetGroup = (args) => {
  const {
    options: { siteId },
    data: { id },
  } = args;
  return api.callGet(`/manager/sites/${siteId}/asset-groups/${id}`, args);
};

export const postAssetGroup = (args) => {
  const {
    options: { siteId },
  } = args;
  return api.callPost(`/manager/sites/${siteId}/asset-groups`, args);
};

export const patchAssetGroup = (args) => {
  const {
    options: { siteId },
    data: { id },
  } = args;
  return api.callPatch(`/manager/sites/${siteId}/asset-groups/${id}`, args);
};

export const archiveAssetGroup = (args) => {
  const {
    options: { siteId },
    data: { id },
  } = args;
  return api.callPost(`/manager/sites/${siteId}/asset-groups/${id}/archive`, {
    options: args.options,
  });
};

export const unarchiveAssetGroup = (args) => {
  const {
    options: { siteId },
    data: { id },
  } = args;
  return api.callPost(`/manager/sites/${siteId}/asset-groups/${id}/unarchive`, {
    options: args.options,
  });
};

export const addGroupChild = (args) => {
  const {
    options: { siteId },
    data: { id },
  } = args;
  return api.callPost(`/manager/sites/${siteId}/asset-groups/${id}/children`, args);
};

export const removeGroupChild = (args) => {
  const {
    options: { siteId },
    data: { id, childGroupId },
  } = args;
  return api.callDelete(`/manager/sites/${siteId}/asset-groups/${id}/children/${childGroupId}`, {
    options: args.options,
  });
};

export const linkGroupItem = (args) => {
  const {
    options: { siteId },
    data: { id },
  } = args;
  return api.callPost(`/manager/sites/${siteId}/asset-groups/${id}/items`, args);
};

export const unlinkGroupItem = (args) => {
  const {
    options: { siteId },
    data: { id, assetItemId },
  } = args;
  return api.callDelete(`/manager/sites/${siteId}/asset-groups/${id}/items/${assetItemId}`, {
    options: args.options,
  });
};

export const getGroupTaskOccurrences = (args) => {
  const {
    options: { siteId },
    data: { id },
  } = args;
  return api.callGet(`/manager/sites/${siteId}/asset-groups/${id}/task-occurrences`, args);
};

export const getAssetItems = (args) => {
  const {
    options: { siteId },
  } = args;
  return api.callGet(`/manager/sites/${siteId}/asset-items`, args);
};

export const getAssetItem = (args) => {
  const {
    options: { siteId },
    data: { id },
  } = args;
  return api.callGet(`/manager/sites/${siteId}/asset-items/${id}`, args);
};

export const postAssetItem = (args) => {
  const {
    options: { siteId },
  } = args;
  return api.callPost(`/manager/sites/${siteId}/asset-items`, args);
};

export const patchAssetItem = (args) => {
  const {
    options: { siteId },
    data: { id },
  } = args;
  return api.callPatch(`/manager/sites/${siteId}/asset-items/${id}`, args);
};

export const archiveAssetItem = (args) => {
  const {
    options: { siteId },
    data: { id },
  } = args;
  return api.callPost(`/manager/sites/${siteId}/asset-items/${id}/archive`, {
    options: args.options,
  });
};

export const unarchiveAssetItem = (args) => {
  const {
    options: { siteId },
    data: { id },
  } = args;
  return api.callPost(`/manager/sites/${siteId}/asset-items/${id}/unarchive`, {
    options: args.options,
  });
};

export const getAssetMaintenance = (args) => {
  const {
    options: { siteId },
  } = args;
  return api.callGet(`/manager/sites/${siteId}/asset-maintenance`, args);
};

export const getAssetMaintenanceItem = (args) => {
  const {
    options: { siteId },
    data: { assetItemId },
  } = args;
  return api.callGet(`/manager/sites/${siteId}/asset-maintenance/${assetItemId}`, args);
};

export const postAssetMaintenance = (args) => {
  const {
    options: { siteId },
  } = args;
  return api.callPost(`/manager/sites/${siteId}/asset-maintenance`, args);
};

export const patchAssetMaintenance = (args) => {
  const {
    options: { siteId },
    data: { assetItemId },
  } = args;
  return api.callPatch(`/manager/sites/${siteId}/asset-maintenance/${assetItemId}`, args);
};

export const archiveAssetMaintenance = (args) => {
  const {
    options: { siteId },
    data: { assetItemId },
  } = args;
  return api.callPost(`/manager/sites/${siteId}/asset-maintenance/${assetItemId}/archive`, {
    options: args.options,
  });
};

export const unarchiveAssetMaintenance = (args) => {
  const {
    options: { siteId },
    data: { assetItemId },
  } = args;
  return api.callPost(`/manager/sites/${siteId}/asset-maintenance/${assetItemId}/unarchive`, {
    options: args.options,
  });
};

export const getTaskOccurrences = (args) => {
  const {
    options: { siteId },
  } = args;
  return api.callGet(`/manager/sites/${siteId}/task-occurrences`, args);
};

export const getTaskOccurrence = (args) => {
  const {
    options: { siteId },
    data: { id },
  } = args;
  return api.callGet(`/manager/sites/${siteId}/task-occurrences/${id}`, args);
};

export const postTaskOccurrence = (args) => {
  const {
    options: { siteId },
  } = args;
  return api.callPost(`/manager/sites/${siteId}/task-occurrences`, args);
};

export const postEnsureTaskOccurrence = (args) => {
  const {
    options: { siteId },
  } = args;
  return api.callPost(`/manager/sites/${siteId}/task-occurrences/ensure`, args);
};

export const patchTaskOccurrence = (args) => {
  const {
    options: { siteId },
    data: { id },
  } = args;
  return api.callPatch(`/manager/sites/${siteId}/task-occurrences/${id}`, args);
};

export const archiveTaskOccurrence = (args) => {
  const {
    options: { siteId },
    data: { id },
  } = args;
  return api.callPost(`/manager/sites/${siteId}/task-occurrences/${id}/archive`, {
    options: args.options,
  });
};

export const completeTaskOccurrence = (args) => {
  const {
    options: { siteId },
    data: { id },
  } = args;
  return api.callPatch(`/manager/sites/${siteId}/task-occurrences/${id}`, {
    ...args,
    data: { ...args.data, status: 'Completed' },
  });
};

export const assignTaskOccurrence = (args) => {
  const {
    options: { siteId },
    data: { id, employeeId },
  } = args;
  return api.callPatch(`/manager/sites/${siteId}/task-occurrences/${id}`, {
    ...args,
    data: { employeeId },
  });
};

export const getSiteInventories = (args) => {
  const {
    options: { siteId },
  } = args;
  return api.callGet(`/manager/sites/${siteId}/inventories`, args);
};

export const patchInventory = (args) => {
  const {
    options: { siteId },
    data: { id },
  } = args;
  return api.callPatch(`/manager/sites/${siteId}/inventories/${id}`, args);
};
